const PGAdapter = require('../../common/pg-adapter')

const BATCH_COLUMNS = [
  'id', 'couchdb_id', 'product_id', 'manufacturer', 'batch_no', 'expiry',
  'alias', 'code', 'created_at', 'created_by', 'updated_at', 'updated_by'
]

class BatchPgAdapter extends PGAdapter {
  // `fromSync` marks the sync-pipeline writer, whose rows are a faithful copy
  // of a couch batch doc: created_at/created_by/updated_at/updated_by are
  // whatever that doc holds, including nothing at all. Stamping PGAdapter's
  // defaults on them would invent metadata the source never had, and
  // pgRowToCouchBatch would then hand callers createdAt/updatedAt fields that
  // don't exist in couch.
  //
  // Every other caller — a user creating or editing a batch through the api —
  // constructs the adapter without the flag and keeps the normal defaults.
  constructor (pgConnection, username, logger, { fromSync = false } = {}) {
    super(
      pgConnection,
      'avocado.batch',
      username,
      BATCH_COLUMNS,
      'id',
      {},
      logger,
      { addUuid: false }
    )
    this.fromSync = fromSync
  }

  // Skipping the whole hook is safe here rather than only the four metadata
  // columns: this adapter passes addUuid: false, and avocado.batch has no
  // organisation_id column, so the rest of applyCreateDefaults is a no-op.
  applyCreateDefaults (data) {
    if (this.fromSync) return
    return super.applyCreateDefaults(data)
  }

  applyUpdateDefaults (data) {
    if (this.fromSync) return
    return super.applyUpdateDefaults(data)
  }

  // No child tables to keep in sync transactionally (unlike funder's
  // program-partner mapping), so a single ON CONFLICT upsert is enough.
  async upsertBatch (batch, { client } = {}) {
    return this.upsert(batch, { conflictIdColumns: ['couchdb_id'], client })
  }

  async setDeleted (couchdbId, { client } = {}) {
    const db = client || this.pgConnection
    return db.query('DELETE FROM avocado.batch WHERE couchdb_id = $1', [couchdbId])
  }

  async getByIds (couchdbIds) {
    if (!couchdbIds || !couchdbIds.length) return []
    const { rows } = await this.pgConnection.query(
      'SELECT * FROM avocado.batch WHERE couchdb_id = ANY($1)',
      [couchdbIds]
    )
    return rows
  }

  // Mirrors the couch Mango range-query in get-product-non-expired-batches.js,
  // scoped by product_id (the couch productId) instead of an _id range hack.
  async nonExpiredForProducts (productIds) {
    if (!productIds || !productIds.length) return []
    const { rows } = await this.pgConnection.query(
      'SELECT * FROM avocado.batch WHERE product_id = ANY($1) AND expiry > now()',
      [productIds]
    )
    return rows
  }

  // Mirrors batch-missing.js's allDocs+not_found filter.
  async missingFromIds (couchdbIds) {
    if (!couchdbIds || !couchdbIds.length) return []
    const { rows } = await this.pgConnection.query(
      'SELECT couchdb_id FROM avocado.batch WHERE couchdb_id = ANY($1)',
      [couchdbIds]
    )
    const found = new Set(rows.map(row => row.couchdb_id))
    return couchdbIds.filter(id => !found.has(id))
  }
}

module.exports = BatchPgAdapter
